/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * useFocalLength.js
 *
 * Composable for managing default focal length settings
 * Used by ML weight estimation pipeline when EXIF data is unavailable
 */

const STORAGE_KEY = 'digiziege_default_focal_length'
const STORAGE_KEY_35MM = 'digiziege_default_focal_35mm'
const DEFAULT_FOCAL_LENGTH = 4.2 // iPhone 12 mini default
const DEFAULT_FOCAL_35MM = 26.0 // iPhone 12 mini default

/**
 * Get the default focal length from user settings
 * @returns {number} Focal length in millimeters
 */
export function getDefaultFocalLength() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = parseFloat(stored)
      if (!isNaN(parsed) && parsed > 0) {
        return parsed
      }
    }
  } catch (err) {
    console.error('Failed to get default focal length:', err)
  }
  return DEFAULT_FOCAL_LENGTH
}

/**
 * Get the default 35mm equivalent focal length from user settings
 * @returns {number} Focal length in millimeters
 */
export function getDefaultFocal35mm() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_35MM)
    if (stored) {
      const parsed = parseFloat(stored)
      if (!isNaN(parsed) && parsed > 0) {
        return parsed
      }
    }
  } catch (err) {
    console.error('Failed to get default focal 35mm:', err)
  }
  return DEFAULT_FOCAL_35MM
}

/**
 * Save sensor settings to user settings
 * @param {number} focal - Focal length in millimeters
 * @param {number|null} focal35mm - 35mm equivalent focal length
 */
export function setDefaultSensorSettings(focal, focal35mm = null) {
  try {
    localStorage.setItem(STORAGE_KEY, focal.toString())
    if (focal35mm) {
      localStorage.setItem(STORAGE_KEY_35MM, focal35mm.toString())
    } else {
      localStorage.removeItem(STORAGE_KEY_35MM)
    }
    return true
  } catch (err) {
    console.error('Failed to save sensor settings:', err)
    return false
  }
}

/**
 * Composable hook for focal length management
 * @returns {Object} Focal length utilities
 */
export function useFocalLength() {
  return {
    getDefaultFocalLength,
    getDefaultFocal35mm,
    setDefaultSensorSettings,
    DEFAULT_FOCAL_LENGTH,
    DEFAULT_FOCAL_35MM,
    STORAGE_KEY,
    STORAGE_KEY_35MM,
  }
}
